// Copyright 2026, Auxilio Technologies LLC

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ordersAPI } from '../services/api';
import { SLOT_ORDER, PAYMENT_STATUSES, NOTIFY_MODES, getLocalDateString } from '../lib/orderUtils';

const inputClass =
  'w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:border-transparent focus:ring-2 focus:ring-rose-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white';
const labelClass = 'mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300';

const AddOrder: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    recipient_name: '',
    recipient_phone: '',
    delivery_address: '',
    delivery_date: getLocalDateString(),
    delivery_time_slot: '',
    arrangement_type: '',
    special_notes: '',
    card_message: '',
    order_value: '',
    payment_status: '',
    notify_mode: 'auto',
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.recipient_name || !form.delivery_address || !form.delivery_time_slot) {
      setError(t('addOrder.requiredError'));
      return;
    }
    setSaving(true);
    try {
      const payload: Record<string, any> = {
        recipient_name: form.recipient_name,
        recipient_phone: form.recipient_phone || null,
        delivery_address: form.delivery_address,
        delivery_date: form.delivery_date,
        delivery_time_slot: form.delivery_time_slot,
        arrangement_type: form.arrangement_type || null,
        special_notes: form.special_notes || null,
        card_message: form.card_message || null,
        order_value_cents: form.order_value ? Math.round(parseFloat(form.order_value) * 100) : null,
        payment_status: form.payment_status || null,
        notify_mode: form.notify_mode,
        source: 'manual',
        // Client-generated id makes the create idempotent if retried.
        external_id: `manual-${Date.now()}-${Math.floor(Math.random() * 100000)}`,
      };
      await ordersAPI.create(payload);
      navigate('/orders');
    } catch (err: any) {
      setError(err.response?.data?.detail || t('auth.errorGeneric'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('addOrder.title')}</h1>
        <p className="mt-1 text-gray-500 dark:text-gray-400">{t('addOrder.subtitle')}</p>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">{error}</div>}

      <form onSubmit={handleSubmit} className="space-y-6 rounded-xl border border-gray-100 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className={labelClass}>{t('addOrder.recipientName')} *</label>
            <input className={inputClass} value={form.recipient_name} onChange={(e) => set('recipient_name', e.target.value)} required />
          </div>
          <div>
            <label className={labelClass}>{t('addOrder.recipientPhone')}</label>
            <input className={inputClass} value={form.recipient_phone} onChange={(e) => set('recipient_phone', e.target.value)} />
          </div>
        </div>

        <div>
          <label className={labelClass}>{t('addOrder.deliveryAddress')} *</label>
          <input className={inputClass} value={form.delivery_address} onChange={(e) => set('delivery_address', e.target.value)} required />
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className={labelClass}>{t('addOrder.deliveryDate')} *</label>
            <input type="date" className={inputClass} value={form.delivery_date} onChange={(e) => set('delivery_date', e.target.value)} required />
          </div>
          <div>
            <label className={labelClass}>{t('addOrder.timeSlot')} *</label>
            <select className={inputClass} value={form.delivery_time_slot} onChange={(e) => set('delivery_time_slot', e.target.value)} required>
              <option value="">{t('addOrder.selectSlot')}</option>
              {SLOT_ORDER.map((s) => (
                <option key={s} value={s}>{t(`order.slot.${s}`)}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className={labelClass}>{t('addOrder.arrangementType')}</label>
            <input className={inputClass} value={form.arrangement_type} onChange={(e) => set('arrangement_type', e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>{t('addOrder.orderValue')}</label>
            <input type="number" min="0" step="0.01" className={inputClass} value={form.order_value} onChange={(e) => set('order_value', e.target.value)} />
          </div>
        </div>

        <div>
          <label className={labelClass}>{t('addOrder.cardMessage')}</label>
          <input className={inputClass} value={form.card_message} onChange={(e) => set('card_message', e.target.value)} />
        </div>

        <div>
          <label className={labelClass}>{t('addOrder.specialNotes')}</label>
          <textarea className={inputClass} rows={2} value={form.special_notes} onChange={(e) => set('special_notes', e.target.value)} />
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className={labelClass}>{t('addOrder.paymentStatus')}</label>
            <select className={inputClass} value={form.payment_status} onChange={(e) => set('payment_status', e.target.value)}>
              <option value="">{t('addOrder.selectPayment')}</option>
              {PAYMENT_STATUSES.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>{t('addOrder.notifyMode')}</label>
            <select className={inputClass} value={form.notify_mode} onChange={(e) => set('notify_mode', e.target.value)}>
              {NOTIFY_MODES.map((m) => (
                <option key={m} value={m}>{t(`order.notify.${m}`)}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button type="submit" disabled={saving} className="rounded-lg bg-rose-600 px-5 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50">
            {saving ? t('addOrder.saving') : t('addOrder.save')}
          </button>
          <button type="button" onClick={() => navigate('/orders')} className="rounded-lg bg-gray-200 px-5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600">
            {t('common.cancel')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddOrder;
